import type { CgtFieldType } from '@/types/cgt'

export interface AnchoredFieldItem {
  /** Unique across the procedure: `${stepId}:${fieldId}` */
  key: string
  fieldId: string
  label: string
  fieldType: CgtFieldType
  formName: string
  value: unknown
}

/** Renders an anchored value as a short, read-only string (or em-dash when empty). */
function formatValue(value: unknown, fieldType: CgtFieldType): string {
  if (value === undefined || value === null || value === '') return '—'
  if (fieldType === 'checkbox') return value === true ? 'Yes' : 'No'
  if (fieldType === 'signature') {
    const sig = value as { signedBy?: string }
    return sig?.signedBy ? `Signed · ${sig.signedBy}` : '—'
  }
  return String(value)
}

/**
 * The procedure header — patient-level anchored fields gathered from every form in
 * the procedure. Fields show even before they have a value so the team can see
 * which key data points are still outstanding.
 */
export function AnchoredFieldsPanel({ items }: { items: AnchoredFieldItem[] }) {
  if (items.length === 0) return null
  return (
    <div className='rounded-xl border border-blue-200 bg-blue-50/50 p-3'>
      <dl className='grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3 lg:grid-cols-4'>
          {items.map(item => {
            const display = formatValue(item.value, item.fieldType)
            const empty = display === '—'
            return (
              <div key={item.key} className='flex min-w-0 items-baseline gap-2'>
                <dt
                  className='shrink-0 truncate text-[11px] font-medium uppercase tracking-wide text-blue-600/70'
                  title={`${item.label} · ${item.formName}`}
                >
                  {item.label}
                </dt>
                <dd
                  className={
                    'min-w-0 truncate text-sm ' +
                    (empty ? 'text-muted-foreground' : 'font-medium text-gray-900')
                  }
                  title={display}
                >
                  {display}
                </dd>
              </div>
            )
          })}
        </dl>
    </div>
  )
}
